<template>
  <div :class="['cursor', { 'u-fade-out': hideCursor }]">
    <div class="pointer" :style="pointerStyle"></div>
    <div class="circle" :style="circleStyle"></div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.cursor {
  backface-visibility: hidden;
  pointer-events: none;
  transition: opacity $dur $ease;
  user-select: none;

  .pointer {
    background: $secondary;
    border-radius: 50%;
    height: 50px;
    left: 0;
    mix-blend-mode: hue;
    opacity: 0.85;
    position: fixed;
    top: 0;
    width: 50px;
    will-change: transform;
    z-index: 1100;
  }

  .circle {
    border-radius: 50%;
    border: solid 1px $secondary;
    height: 60px;
    left: 0;
    mix-blend-mode: hue;
    opacity: 0.85;
    position: fixed;
    top: 0;
    width: 60px;
    will-change: transform;
    z-index: 1100;
  }
}
</style>

<script>
import { hasFinePointer } from '@/pointer';

export default {
  data() {
    return {
      frame: 0,
      hideCursor: true,
      hover: false,
      mouse: {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      },
      pointer: {
        scale: 1 / 5,
        size: 50,
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      },
      circle: {
        scale: 1 / 2,
        size: 60,
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      }
    };
  },

  computed: {
    circleStyle() {
      return `transform: translateX(${this.circle.x}px) translateY(${this.circle.y}px) scale(${this.circle.scale});`;
    },

    pointerStyle() {
      return `transform: translateX(${this.pointer.x}px) translateY(${this.pointer.y}px) scale(${this.pointer.scale});`;
    }
  },

  methods: {
    lerp(start, end, amount) {
      return (1 - amount) * start + amount * end;
    },

    updateCursor() {
      var pointerScaleRatio = 1 / 5,
        circleScaleRatio = 1 / 2;

      this.pointer.scale = this.lerp(this.pointer.scale, this.hover ? 1 : pointerScaleRatio, 0.15);
      this.pointer.x = this.lerp(this.pointer.x, this.mouse.x - this.pointer.size / 2, 0.75);
      this.pointer.y = this.lerp(this.pointer.y, this.mouse.y - this.pointer.size / 2, 0.75);

      this.circle.scale = this.lerp(this.circle.scale, this.hover ? 1 : circleScaleRatio, 0.15);
      this.circle.x = this.lerp(this.circle.x, this.mouse.x - this.circle.size / 2, 0.15);
      this.circle.y = this.lerp(this.circle.y, this.mouse.y - this.circle.size / 2, 0.15);

      this.frame = requestAnimationFrame(this.updateCursor);
    },

    handleMove(e) {
      /* Any movement means the pointer is inside the window, so the cursor is
         shown from here. It used to be unhidden only by a `mouseenter` on the
         document, which never fires again after an Inertia navigation: the
         component remounts with the cursor hidden and the pointer is already
         inside, so the native cursor stayed alone until the reader happened to
         leave the window and come back. */
      this.hideCursor = false;

      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
      /* `closest` walks up from the element the pointer is actually over, so the
         hover test costs one short walk. This used to run
         `document.querySelectorAll(':hover')`, which asks the browser to match
         the hover state against the whole document on every mousemove. */
      this.hover = Boolean(e.target.closest && e.target.closest('a, button'));
    },

    hideCursorOnLeave() {
      this.hideCursor = true;
    },

    showCursorOnEnter() {
      this.hideCursor = false;
    }
  },

  created() {
    /* The custom cursor is a trailing, eased pointer. It runs for every visitor
       who has a pointer to trail; motion is always shown on this site, so the
       only condition is the hardware. A device with no fine pointer gets the
       native cursor instead: there is no hover to trail, and the one mousemove
       a tap fires would strand the cursor where the finger landed. The element
       stays hidden (`hideCursor` defaults to true, and nothing is listening to
       unhide it). */
    this.enabled = hasFinePointer();

    if (!this.enabled) {
      return;
    }

    window.addEventListener('mousemove', this.handleMove);
  },

  beforeUnmount() {
    window.removeEventListener('mousemove', this.handleMove);
    document.removeEventListener('mouseleave', this.hideCursorOnLeave);
    document.removeEventListener('mouseenter', this.showCursorOnEnter);
    /* The loop below is a self-scheduling rAF, so leaving it running after the
       component is gone keeps a transform write per frame for the rest of the
       session. */
    cancelAnimationFrame(this.frame);
  },

  mounted() {
    if (!this.enabled) {
      return;
    }

    document.addEventListener('mouseleave', this.hideCursorOnLeave);
    document.addEventListener('mouseenter', this.showCursorOnEnter);

    this.updateCursor();
  }
};
</script>
